import {Component, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {RouterLink} from '@angular/router';
import {forkJoin, Subscription} from 'rxjs';
import {faStar} from '@fortawesome/pro-solid-svg-icons';

import {SidebarItemComponent} from '../../util/design/sidebar/sidebar-item/sidebar-item.component';
import {CheckPinService, PinnedCheck} from '../../../services/check-pin.service';
import {StateConfigService} from '../../../services/state/state-config.service';
import {WorkflowService} from '../../../services/workflow.service';
import {UserPreferenceService} from '../../../services/user-preference.service';

/**
 * Rendert die angehefteten Checks als Shortcuts in der Seitenleiste.
 * Lädt die Pins des aktuellen Users und aktualisiert sich bei Pin-Änderungen
 * sowie bei Projektwechsel.
 */
@Component({
  selector: 'app-pinned-checks-sidebar',
  standalone: true,
  imports: [SidebarItemComponent, RouterLink],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: `
    <div class="flex flex-col gap-1 w-full">
      @for (pin of pins; track pin.pinId) {
        <app-sidebar-item
          [collapsed]="collapsed"
          [opened]="opened"
          [icon]="faThumbtack"
          [title]="collapsed ? '' : pin.name"
          [selected]="'definitionId=' + pin.definitionId"
          [routerLink]="['projects', currentProjectId, 'check-runs', 'execute']"
          [queryParams]="{ definitionId: pin.definitionId }"
          (click)="$event.stopPropagation()"
          class="w-full">
        </app-sidebar-item>
      }
    </div>
  `,
})
export class PinnedChecksSidebarComponent implements OnInit, OnDestroy {
  @Input() collapsed = false;
  @Input() opened = true;

  currentProjectId = 0;
  pins: PinnedCheck[] = [];
  protected readonly faThumbtack = faStar;

  private pinService = inject(CheckPinService);
  private stateConfig = inject(StateConfigService);
  private workflowService = inject(WorkflowService);
  private prefService = inject(UserPreferenceService);
  private subscriptions = new Subscription();

  ngOnInit(): void {
    this.loadPins();
    // Bei Pin-Änderungen neu laden
    this.subscriptions.add(this.pinService.pinsChanged$.subscribe(() => this.loadPins()));
    // Bei Projektwechsel neu laden (Sichtbarkeit kann sich ändern)
    this.subscriptions.add(this.stateConfig.projectChange.subscribe(() => this.loadPins()));
    // Bei Definition-Updates neu laden (z.B. Namensänderung im Builder)
    this.subscriptions.add(this.workflowService.definitionUpdated$.subscribe(() => this.loadPins()));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private loadPins(): void {
    const projectId = this.stateConfig.getProjectId();
    this.currentProjectId = projectId;
    forkJoin([
      this.pinService.listPins(projectId),
      this.prefService.get(projectId),
    ]).subscribe({
      next: ([pins, rawOrder]) => {
        const visible = pins.filter(pin => pin.scope === 'ORGANISATION' || pin.projectId === projectId);
        const savedOrder = this.parseOrder(rawOrder);
        if (savedOrder.length) {
          this.pins = [
            ...savedOrder.map(id => visible.find(p => p.definitionId === id)).filter((p): p is PinnedCheck => !!p),
            ...visible.filter(p => !savedOrder.includes(p.definitionId)),
          ];
        } else {
          this.pins = visible;
        }
      },
      error: () => (this.pins = []),
    });
  }

  private parseOrder(raw: string | null): string[] {
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  }
}
